<template>
    <form-fields>
        <toolbar-buttons
            :no-scroll="true"
        >
            <div class="cz3__image-upload__outer">
                <div class="cz3__image-upload">
                    <input
                        ref="uploader"
                        type="file"
                        class="cz3__image-upload__input"
                        :accept="mimeTypes.join(',')"
                        @change="uploadPicked"
                    />

                    <span v-if="fileName" class="cz3__image-upload__name">
                        {{fileName}}
                    </span>

                    <span v-else class="cz3__image-upload__name">
                        {{$t('V3_EDIT_UPLOAD_EMPTY')}}
                    </span>
                </div>

                <div class="cz3__image-upload__hint">
                    <span v-if="personalizationMethod === 'etching'">
                        {{$t('V3_IMAGE_UPLOAD_ETCHING')}}
                    </span>

                    <span v-if="personalizationMethod === 'printing'">
                        {{$t('V3_IMAGE_UPLOAD_PRINTING')}}
                    </span>
                </div>
            </div>

            <transition name="cz2__fade">
                <div v-if="uploadWorking" class="upload-busy" ref="spinner-container">
                    <div ref="spinner" class="upload-busy__spinner">
                        <!-- eslint-disable -->
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.6.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc.--><path d="M224 32c0-17.7 14.3-32 32-32C397.4 0 512 114.6 512 256c0 46.6-12.5 90.4-34.3 128c-8.8 15.3-28.4 20.5-43.7 11.7s-20.5-28.4-11.7-43.7c16.3-28.2 25.7-61 25.7-96c0-106-86-192-192-192c-17.7 0-32-14.3-32-32z"/></svg>
                        <!-- eslint-enable -->
                    </div>
                </div>
            </transition>
        </toolbar-buttons>

        <step-buttons
            :class="{
                'cz3__hide-lg': buttonsSm,
            }"
        >
            <secondary-button
                class="cz3__flex-1"
                :label="$t('V3_BACK')"
                @click="back"
            />

            <action-button
                class="cz3__flex-1"
                :label="$t('V3_NEXT')"
                :disabled="editComponent == null || uploadWorking"
                @click="next"
            />
        </step-buttons>
    </form-fields>
</template>

<style lang="scss">
    @import "../../style/variables.scss";

    #cz2.cz2 {
        .cz3__image-upload {
            position: relative;

            width: 100%;
            height: 68px;
            padding: 0 16px;

            display: flex;
            justify-content: center;
            align-items: center;

            background: #f5f5f5;
            border-radius: 8px;

            &:focus-within {
                outline: 2px solid #000;
                outline-offset: 2px;
            }
        }

        @keyframes cz3__spin-forever {
            0% {
                transform: rotate(0deg);
            }

            100% {
                transform: rotate(360deg);
            }
        }

        .cz3__upload-busy {
            position: absolute;

            left: 0;
            top: 0;

            width: 100%;
            height: 100%;

            background: rgba(255, 255, 255, 0.8);

            svg {
                width: 40px;
                height: 40px;

                animation: cz3__spin-forever 2s linear infinite;
            }
        }

        .cz3__upload-busy__spinner {
            position: absolute;

            left: 50%;
            top: 50%;

            transform: translate(-50%, -50%);
        }

        .cz3__image-upload__outer {
            width: 100%;
        }

        .cz3__image-upload__hint {
            margin-top: 8px;

            font-family: $font-main;
            font-weight: 400;
            font-size: 12px;
        }

        .cz3__image-upload__input {
            position: absolute;

            left: 0;
            top: 0;

            width: 100%;
            height: 100%;

            border-radius: 8px;

            background: #f00;

            opacity: 0;
        }

        .cz3__image-upload__name {
            flex: 1 0 0;

            max-width: calc(100% - 32px);

            font-family: $font-main;
            font-weight: 700;
            font-size: 14px;

            color: #000;

            text-align: center;

            pointer-events: none;

            white-space: nowrap;

            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
</style>

<script>
    import axios from 'axios';
    import Vue from 'vue';

    import ToolbarButtons from './ToolbarButtons.vue';
    import FormFields from './FormFields.vue';
    import ActionButton from './ActionButton.vue';
    import SecondaryButton from './SecondaryButton.vue';
    import StepButtons from './StepButtons.vue';

    import isFilled from '../../utilities/isFilled';
    import * as constants from '../../constants';

    // eslint-disable-next-line no-unused-vars
    import designArea from '../../utilities/designArea';
    // eslint-disable-next-line no-unused-vars
    import extent from '../../utilities/extent';

    export default {
        components: {
            ToolbarButtons,
            FormFields,
            ActionButton,
            SecondaryButton,
            StepButtons,
        },

        data() {
            return {
                selectedText: '',

                fileName: null,

                uploadWorking: false,
            };
        },

        props: {
            buttonsSm: Boolean,
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            currentView() {
                return this.$store.state.customizer.currentView;
            },

            editPlacements() {
                return this.$store.state.customizer.editPlacements;
            },

            editPlacement() {
                if (this.editPlacements && this.editPlacements.length > 0) {
                    return this.editPlacements[0];
                }

                return null;
            },

            editComponent() {
                if (this.editPlacement) {
                    const selected = this.viewController.selectedAtPlacement(this.editPlacement.code);

                    if (isFilled(selected)) {
                        return selected;
                    }
                }

                return null;
            },

            filledTextPlacements() {
                return this.$store.getters.filledTextPlacements;
            },

            emptyTextPlacements() {
                return this.$store.getters.emptyTextPlacements;
            },

            filledPlacements() {
                return this.$store.getters.filledPlacements;
            },

            /**
             * Gets a list of free images.
             */
            emptyImagePlacements() {
                return this.$store.getters.emptyImagePlacements;
            },

            personalizationMethod() {
                return this.$store.state.customizer.personalizationMethod;
            },

            nextStep() {
                return this.$store.getters.nextStepV3;
            },

            /**
             * Calculate MIME types supported by the personalization.
             *
             * Used by the Open File dialog.
             */
            mimeTypes() {
                if (this.personalizationMethod === 'printing') {
                    return ['image/tiff', 'image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf', '.ai'];
                }

                return ['image/tiff', 'image/png', 'image/svg+xml', 'application/pdf', '.ai'];
            },

            /**
             * Calculate MIME types supported by the personalization.
             *
             * Used for validation.
             */
            mimeTypesSelected() {
                if (this.personalizationMethod === 'printing') {
                    return ['image/tiff', 'image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf', 'application/postscript'];
                }

                return ['image/tiff', 'image/png', 'image/svg+xml', 'application/pdf', 'application/postscript'];
            },

            /**
             * A list of file extensions to block.
             */
            blockExtensions() {
                return ['.eps'];
            },

            /**
             * Secondary bottles array.
             */
            secondaryBottles() {
                return this.$store.state.customizer.secondaryBottles;
            },
        },

        mounted() {
            if (this.editComponent != null) {
                if (this.editComponent.custom?.['dynamic-image-original-name']) {
                    this.fileName = this.editComponent.custom?.['dynamic-image-original-name'];
                }
            }
        },

        methods: {
            back() {
                this.$store.dispatch('setSelectedSubStepV3', 'choose');
            },

            next() {
                this.$store.dispatch('setSelectedSubStepV3', 'upload-alignment');
            },

            loadFromPlacement() {
                this.selectedText = this.editComponent.custom['personalization-text'] || '';

                if (this.editComponent.custom['v-design-on']) {
                    this.$store.dispatch('switchToDesignView', this.editComponent.custom['v-design-on']);
                }
            },

            uploadPicked() {
                if (this.$refs.uploader.files.length > 0) {
                    let placement = {
                        placement: this.editPlacement,
                        component: this.editComponent,
                    };

                    if (placement?.placement == null && this.emptyImagePlacements.length > 0) {
                        // eslint-disable-next-line prefer-destructuring
                        placement = this.emptyImagePlacements[0];
                    }

                    if (placement) {
                        this.handleFileImage(this.$refs.uploader.files[0], {
                            targetPlacement: placement,
                        });
                    }

                    this.$refs.uploader.value = null;
                }
            },

            handleFileImageContinue(file, options, resolve) {
                // Validate the file type.

                if (file instanceof File) {
                    if (!this.mimeTypesSelected.includes(file.type) || this.isBlockedExtension(file.name)) {
                        if (this.personalizationMethod === constants.METHOD_PRINTING) {
                            this.$store.dispatch('showGenericNotify', {
                                title: this.$t('IMAGE_UPLOAD_BAD_TYPE_PRINTING'),
                                description: this.$t('IMAGE_UPLOAD_BAD_TYPE_PRINTING_COPY'),
                                action: this.$t('ACTION_CONTINUE'),
                            });
                        } else {
                            this.$store.dispatch('showGenericNotify', {
                                title: this.$t('IMAGE_UPLOAD_BAD_TYPE_ETCHING'),
                                description: this.$t('IMAGE_UPLOAD_BAD_TYPE_ETCHING_COPY'),
                                action: this.$t('ACTION_CONTINUE'),
                            });
                        }

                        return;
                    }

                    if (this.personalizationMethod === 'printing') {
                        if (file.type.includes('jpeg')) {
                            this.$store.dispatch('showGenericNotify', {
                                title: this.$t('IMAGE_UPLOAD_JPEG'),
                                description: this.$t('IMAGE_UPLOAD_JPEG_COPY'),
                                action: this.$t('ACTION_CONTINUE'),
                            });
                        }
                    }
                }

                const formData = new FormData();

                if (file instanceof File) {
                    formData.append('file', file);
                } else {
                    formData.append('copy-from', file);
                }

                const target = options.targetPlacement.component.custom;

                const imageMinHeight = target && target['dynamic-image-upload-limit-min-height'] ? +target['dynamic-image-upload-limit-min-height'] : null;
                const imageMaxHeight = target && target['dynamic-image-upload-limit-max-height'] ? +target['dynamic-image-upload-limit-max-height'] : null;

                const imageMinWidth = target && target['dynamic-image-upload-limit-min-width'] ? +target['dynamic-image-upload-limit-min-width'] : null;
                const imageMaxWidth = target && target['dynamic-image-upload-limit-max-width'] ? +target['dynamic-image-upload-limit-max-width'] : null;

                if (imageMinHeight && imageMinWidth) {
                    formData.append('min-width', imageMinWidth);
                    formData.append('min-height', imageMinHeight);
                }

                if (imageMaxHeight && imageMaxWidth) {
                    formData.append('max-width', imageMaxWidth);
                    formData.append('max-height', imageMaxHeight);
                }

                const fit = target && target['dynamic-image-upload-fit'] ? +target['dynamic-image-upload-fit'] : null;
                const fitWidth = target && target['dynamic-image-upload-fit-width'] ? +target['dynamic-image-upload-fit-width'] : null;
                const fitHeight = target && target['dynamic-image-upload-fit-height'] ? +target['dynamic-image-upload-fit-height'] : null;

                if (fit && fitWidth && fitHeight) {
                    formData.append('fit', fit);
                    formData.append('fit-width', fitWidth);
                    formData.append('fit-height', fitHeight);
                }

                let trace = false;

                if (this.personalizationMethod === 'etching' && file instanceof File) {
                    const fileName = file.name.toLowerCase();

                    if (fileName.endsWith('.png') || fileName.endsWith('.jpg')) {
                        trace = true;
                    }
                }

                // if (this.viewController.blueprint.custom['v-allow-tracing-v3'] !== 'true') {
                //     trace = false;
                // }

                if (trace) {
                    formData.append('trace', 'true');

                    formData.append('trace-threshold', '0.5');

                    formData.append('bgc', 'ffffff');

                    // formData.append('trace', 'true');

                    // let traceThreshold = '0.6';

                    // if (this.viewController.blueprint.custom['v-tracing-threshold-v3']) {
                    //     traceThreshold = this.viewController.blueprint.custom['v-tracing-threshold-v3'];
                    // }

                    // formData.append('trace-threshold', traceThreshold);

                    // let traceRemoveBackground = 'true';

                    // if (this.viewController.blueprint.custom['v-tracing-remove-background-v3']) {
                    //     traceRemoveBackground = this.viewController.blueprint.custom['v-tracing-remove-background-v3'];
                    // }

                    // formData.append('trace-remove-background', traceRemoveBackground);

                    // let traceInvert = 'true';

                    // if (this.viewController.blueprint.custom['v-tracing-invert-v3']) {
                    //     traceInvert = this.viewController.blueprint.custom['v-tracing-invert-v3'];
                    // }

                    // formData.append('trace-invert', traceInvert);

                    formData.append('minrasterwidth', '2000');
                    formData.append('minrasterheight', '2000');

                    formData.append('jpeg-preview-size', '500');

                    formData.append('jpeg-preview-background', '255,255,255,255');
                } else {
                    formData.append('min-raster-width', constants.MIN_RASTER_WIDTH);
                    formData.append('min-raster-height', constants.MIN_RASTER_HEIGHT);
                }

                if (options && options.type === 'gallery') {
                    formData.append('allow-reuse', 'true');
                }

                this.uploadWorking = true;
                this.uploadProgress = 0;

                axios({
                    method: 'post',
                    url: '//fx.images.drivecommerce.com/api/v1/image/logomark/upload/',
                    data: formData,
                    config: {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    },
                    onUploadProgress: (progressEvent) => {
                        this.uploadProgress = (progressEvent.loaded / progressEvent.total) * 100;
                    },
                }).then((response) => {
                    this.uploadWorking = false;

                    resolve(this.handleImageResponse(response, file, options));
                }).catch((error) => {
                    this.uploadWorking = false;

                    if (error && error.response && error.response.data) {
                        const { data } = error.response;

                        if (data.modelState && data.modelState[''] && data.modelState[''].length > 0) {
                            if (data.details && data.details.error === 'maximum-dimensions') {
                                this.$store.dispatch('showGenericNotify', {
                                    title: this.$t('IMAGE_UPLOAD_BAD_SIZE'),
                                    description: this.$t('IMAGE_UPLOAD_BAD_SIZE_COPY'),
                                    action: this.$t('ACTION_CONTINUE'),
                                });
                            } else if (data.details && data.details.error === 'font-outlines') {
                                this.$store.dispatch('showGenericNotify', {
                                    title: this.$t('IMAGE_UPLOAD_BAD_OUTLINES'),
                                    description: this.$t('IMAGE_UPLOAD_BAD_OUTLINES_COPY'),
                                    action: this.$t('ACTION_CONTINUE'),
                                });
                            } else {
                                this.$store.dispatch('showGenericNotify', {
                                    title: this.$t('V3_UPLOAD_ISSUE'),
                                    description: data.modelState[''][0],
                                    action: this.$t('ACTION_CONTINUE'),
                                });
                            }
                        }
                    }
                });
            },

            /**
             * Handles image response.
             */
            handleImageResponse(response, file, {
                type,
                targetPlacement,
                name,
                category,
                flags,
            }) {
                this.$store.dispatch('validateEditableViews');
                this.$store.dispatch('switchToCommonView');

                const image = response.data;

                // Pick a placement to put image to.
                const { placement, component } = targetPlacement;

                this.$store.dispatch('takeEmptyPlacement', placement);

                const { view } = this.currentView;

                const designOn = view.custom['v-design-area'];

                const {
                    left, right, top, bottom,
                } = designArea(this.$store.state.customizer, view.custom['v-design-area']);

                if (left != null && right != null && top != null && bottom != null) {
                    // Figure out initial scaling to fit into the design area.
                    const areaWidth = right - left;
                    const areaHeight = bottom - top;

                    this.viewController.commitCustomAttributes(placement.code, {
                        'dynamic-image-area-width': areaWidth.toString(),
                        'dynamic-image-area-height': areaHeight.toString(),
                    });
                }

                // Configure the image.
                this.viewController.updateDynamicImage(placement.code, {
                    title: image.title,
                    name: image.name,
                    url: image.imageUrl,
                    pngUrl: image.pngImageUrl,
                    jpgUrl: image.jpgImageUrl,
                    width: image.width,
                    height: image.height,
                });

                this.$store.dispatch('renderOnlyOnView', {
                    view: this.currentView,
                });

                const group = this.viewController.blueprint.custom[`v-group-${view.custom['v-design-area']}`];

                this.$store.dispatch('setGroup', { placement: placement.code, group });

                // Update dynamic image will center the image; zero out coordinates.
                this.viewController.commitCustomAttributes(placement.code, {
                    'v-design-on': view.custom['v-design-area'],
                    mask: view.custom['v-design-mask'],
                    'dynamic-image-x': 0,
                    'dynamic-image-y': 0,
                    'effect-colorize': null,
                    'effect-colorize-add': null,
                    'effect-colorize-name': null,
                });

                // Is it colorizable?
                let allowColors = true;

                if ((flags || '').includes('N')) {
                    allowColors = false;
                }

                this.viewController.commitCustomAttributes(placement.code, {
                    'allow-colors': allowColors ? 'true' : 'false',
                });

                // If colorizable, pick the first primary color.
                if (type === 'gallery') {
                    if (allowColors && this.personalizationMethod !== 'etching') {
                        let useColor = null;

                        if (this.primaryColors.length > 0) {
                            // eslint-disable-next-line
                            useColor = this.primaryColors[0];
                        }

                        if (useColor == null && this.availableColors.length > 0) {
                            // eslint-disable-next-line
                            useColor = this.availableColors[0];
                        }

                        this.viewController.commitCustomAttributes(placement.code, {
                            'effect-colorize': '000000',
                            'effect-colorize-add': useColor.custom.hex,
                            'effect-colorize-name': useColor.name,
                        });
                    }
                }

                // ... and center manually in the current view.
                if (view.custom) {
                    if (left != null && right != null && top != null && bottom != null) {
                        // Figure out initial scaling to fit into the design area.
                        const areaWidth = right - left;
                        const areaHeight = bottom - top;

                        const imageWidth = +component.custom['dynamic-image-width'];
                        const imageHeight = +component.custom['dynamic-image-height'];

                        const fitScaleWidth = areaWidth / imageWidth;
                        const fitScaleHeight = areaHeight / imageHeight;

                        const fitScale = Math.min(fitScaleWidth, fitScaleHeight);

                        let yOffset = null;
                        let yOffsetMin = null;

                        this.filledPlacements.forEach((p) => {
                            if (this.personalizationMethod === 'printing') {
                                return;
                            }

                            if (p.placement.code === placement.code) {
                                return;
                            }

                            if (p.component.custom['v-design-on'] !== designOn) {
                                return;
                            }

                            if (p.component.custom['personalization-y'] != null) {
                                if (p.placement.code.startsWith(constants.PLACEMENT_MONOGRAM)) {
                                    const below = +p.component.custom['personalization-y'] + (+p.component.custom['personalization-height']) * 1.5;

                                    if (yOffset == null) {
                                        yOffset = below;
                                    } else {
                                        yOffset = Math.max(below, yOffset);
                                    }

                                    const above = +p.component.custom['personalization-y'] - (+p.component.custom['personalization-height']) * 0.5;

                                    if (yOffsetMin == null) {
                                        yOffsetMin = above;
                                    } else {
                                        yOffsetMin = Math.min(above, yOffsetMin);
                                    }
                                } else {
                                    const below = +p.component.custom['personalization-y'] + (+p.component.custom['personalization-height']) * 1.5;

                                    if (yOffset == null) {
                                        yOffset = below;
                                    } else {
                                        yOffset = Math.max(below, yOffset);
                                    }

                                    const above = +p.component.custom['personalization-y'] - (+p.component.custom['personalization-height']) * 0.5;

                                    if (yOffsetMin == null) {
                                        yOffsetMin = above;
                                    } else {
                                        yOffsetMin = Math.min(above, yOffsetMin);
                                    }
                                }
                            }

                            if (p.component.custom['dynamic-image-height'] != null) {
                                const y = +p.component.custom['dynamic-image-offset-y'];
                                const h = +p.component.custom['dynamic-image-height'];
                                const s = +p.component.custom['dynamic-image-scale'];
                                const totalHeight = +p.component.custom['dynamic-image-area-height'];

                                const below = y + (h * s * 0.5) + (totalHeight * 0.1);

                                if (yOffset == null) {
                                    yOffset = below;
                                } else {
                                    yOffset = Math.max(below, yOffset);
                                }

                                const above = y - (h * s * 0.5) - (totalHeight * 0.1);

                                if (yOffsetMin == null) {
                                    yOffsetMin = above;
                                } else {
                                    yOffsetMin = Math.min(above, yOffsetMin);
                                }
                            }
                        });

                        if (yOffset == null) {
                            if ((this.viewController.blueprint.custom['v-default-alignment-v3'] || '').toLowerCase() === 'top') {
                                yOffset = (imageHeight * fitScale * 0.5) + 10;
                            } else {
                                yOffset = ((top + bottom) * 0.5);
                            }
                        } else {
                            // eslint-disable-next-line no-lonely-if
                            if (yOffset + imageHeight * fitScale > bottom) {
                                if (yOffsetMin < top + (imageHeight * fitScale) * 0.25) {
                                    yOffset = ((top + bottom) * 0.5);
                                } else {
                                    yOffset = yOffsetMin - (imageHeight * fitScale * 0.5);
                                }
                            } else {
                                yOffset += imageHeight * fitScale * 0.5;
                            }
                        }

                        this.viewController.commitCustomAttributes(placement.code, {
                            'dynamic-image-offset-x': ((left + right) * 0.5),
                            'dynamic-image-offset-y': yOffset,
                            'dynamic-image-scale': fitScale.toString(),
                        });
                    }
                }

                if (type === 'gallery') {
                    Vue.set(component.custom, 'gallery-image', 'true');
                    if (name) {
                        Vue.set(component.custom, 'gallery-image-name', name);
                    } else {
                        Vue.set(component.custom, 'gallery-image-name', null);
                    }

                    if (category) {
                        Vue.set(component.custom, 'gallery-image-category', category);
                    } else {
                        Vue.set(component.custom, 'gallery-image-category', null);
                    }
                } else {
                    Vue.set(component.custom, 'gallery-image', 'false');
                }

                if (file instanceof File) {
                    this.fileName = file.name;

                    Vue.set(component.custom, 'dynamic-image-original-name', file.name);
                }

                this.beginEdit(placement, component);

                return component;
            },

            beginEdit(placement) {
                this.$store.dispatch('editPlacement', placement);
            },

            /**
             * Handle image upload.
             */
            handleFileImage(file, options) {
                return new Promise((resolve) => {
                    if (file instanceof File) {
                        this.handleFileImageContinue(file, options, resolve);
                    }
                });
            },

            isBlockedExtension(name) {
                const dot = name.lastIndexOf('.');

                if (dot >= 0) {
                    const ext = name.substr(dot).toLowerCase();

                    if (this.blockExtensions.includes(ext)) {
                        return true;
                    }
                }

                return false;
            },
        },
    };
</script>
